import { AnalyticsBrowser, SegmentEvent } from '@segment/analytics-next';

import {
  enrichIdentityProperties,
  type IdentityTraits,
  mergeIdentityTraits,
} from './analyticsIdentity';
import { redactPageAddress } from './deepLinkAnalytics';
import { env } from './env';
import { BUILD_CONTEXT_KEYS, type BuildContextKey, getBuildContext } from './getBuildInformation';
import { isTestEmail } from './isTestEmail';

import { AnalyticCustomKeys, ANALYTICS_CUSTOM_KEYS } from '@/constants/analytics';
import { isAnalyticsDisabledSync } from '@/util/analyticsState';

/**
 * Segment analytics wrapper (web) with platform-aware event filtering.
 * Aligns with native wrapper and includes enrichment middleware.
 */

/** Runtime toggle for Segment instrumentation. */
const isSegmentEnvEnabled = env.EXPO_PUBLIC_SEGMENT_ENABLED !== 'false';

const createWebAnalyticsClient = (): ReturnType<typeof AnalyticsBrowser.load> | null => {
  if (!isSegmentEnvEnabled) {
    return null;
  }

  /**
   * Cookie domain strategy:
   * - Prod (`app.primum.co`): use `.primum.co` so cookies are shared with
   *   the marketing site (`primum.co`) for cross-site identity stitching.
   * - Non-prod: use the exact hostname to avoid contaminating prod/marketing cookies.
   */
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  const isProductionHostname = hostname === 'app.primum.co';

  return AnalyticsBrowser.load(
    {
      writeKey: env.EXPO_PUBLIC_SEGMENT_WEB_WRITE_KEY!,
    },
    {
      obfuscate: true,
      cookie: { domain: isProductionHostname ? '.primum.co' : hostname || undefined },
    }
  );
};

const analytics = createWebAnalyticsClient();

type SegmentWebClient = ReturnType<typeof AnalyticsBrowser.load>;

const isSegmentUsable = (webClient: SegmentWebClient | null): webClient is SegmentWebClient =>
  isSegmentEnvEnabled && !isAnalyticsDisabledSync() && webClient !== null;

function getEmailFromEvent(event: SegmentEvent) {
  return event.traits?.email;
}

/**
 * Keys and values the enrichment middleware may attach to an event. Build fields
 * carry strings and an explicit null, so this is wider than the numeric and
 * boolean custom keys it started as.
 */
type EnrichableKey = AnalyticCustomKeys | BuildContextKey;

type EnrichableValue = string | number | boolean | null;

const updateProperties = (obj: Record<string, any>, key: EnrichableKey, value: EnrichableValue) => {
  if (obj.context) {
    obj.context[key] = value;
  }

  if (obj.properties) {
    obj.properties[key] = value;
  }

  if (obj.traits) {
    obj.traits[key] = value;
  }
};

const PAGE_ADDRESS_KEYS = ['url', 'search', 'referrer'] as const;

/** Masks credentials in the addresses Segment reads from the browser location itself. */
function redactPageAddresses(target: Record<string, unknown> | undefined) {
  if (!target) {
    return;
  }

  PAGE_ADDRESS_KEYS.forEach((key) => {
    const value = target[key];

    if (typeof value === 'string') {
      target[key] = redactPageAddress(value);
    }
  });
}

let userTraits: IdentityTraits = {};

let identifiedUserId: string | undefined;

if (typeof window !== 'undefined' && analytics) {
  let testUserId: string | undefined | null;

  analytics.addSourceMiddleware(({ payload, next }) => {
    redactPageAddresses(payload.obj.properties);

    redactPageAddresses(payload.obj.context?.page);

    next(payload);
  });

  analytics.addSourceMiddleware(({ payload, next }) => {
    updateProperties(payload.obj, ANALYTICS_CUSTOM_KEYS.APP_GENERATION_IDENTIFIER, 2);

    next(payload);
  });

  analytics.addSourceMiddleware(({ payload, next }) => {
    if (payload.obj.type === 'identify') {
      const email = getEmailFromEvent(payload.obj);

      if (email && isTestEmail(email)) {
        testUserId = payload.obj.userId;
      } else {
        testUserId = undefined;
      }
    }

    const isTestUserEvent = testUserId && testUserId === payload.obj.userId;

    if (isTestUserEvent) {
      updateProperties(payload.obj, ANALYTICS_CUSTOM_KEYS.IS_TEST_USER_EVENT, true);
    }

    next(payload);
  });

  /**
   * Enriches all track events with identity fields from the most recent
   * identify() call. The merge/derive/inject logic is shared with the native
   * plugin via `analyticsIdentity.ts` so the two transports can't drift.
   */
  analytics.addSourceMiddleware(({ payload, next }) => {
    if (payload.obj.type === 'identify') {
      const incomingUserId = payload.obj.userId ?? undefined;

      const sameUser = incomingUserId === identifiedUserId;

      identifiedUserId = incomingUserId;

      if (payload.obj.traits) {
        userTraits = mergeIdentityTraits(
          userTraits,
          {
            email: payload.obj.traits.email as string | undefined,
            firstName: payload.obj.traits.firstName as string | undefined,
            lastName: payload.obj.traits.lastName as string | undefined,
            name: payload.obj.traits.name as string | undefined,
            userType: payload.obj.traits.userType as string | undefined,
          },
          sameUser
        );
      }
    }

    if (payload.obj.type === 'track' && payload.obj.properties) {
      enrichIdentityProperties(payload.obj.properties, identifiedUserId, userTraits);
    }

    next(payload);
  });

  /**
   * Attaches build identity to event properties and identify traits, mirroring
   * the native plugin. Writing it onto `context` alone reaches no destination:
   * that object has a fixed shape destinations map from, and custom keys bolted
   * onto it are not forwarded.
   */
  analytics.addSourceMiddleware(({ payload, next }) => {
    const buildContext = getBuildContext();

    BUILD_CONTEXT_KEYS.forEach((key) => {
      const value = buildContext[key];

      if (value !== undefined) {
        updateProperties(payload.obj, key, value);
      }
    });

    next(payload);
  });
}

type Platform = 'native' | 'web';

type EventConfig = { name: string; platforms?: readonly Platform[] };

type EventInput = string | EventConfig;

const isEventAllowed = (event: EventInput, platform: Platform): boolean => {
  if (typeof event === 'string') {
    return true;
  }

  if (!event.platforms) {
    return true;
  }

  return event.platforms.includes(platform);
};

const getEventName = (event: EventInput): string =>
  typeof event === 'string' ? event : event.name;

export const segmentClient = {
  track: (event: EventInput, properties?: Parameters<SegmentWebClient['track']>[1]) => {
    if (!isSegmentUsable(analytics)) {
      return;
    }

    if (!event) {
      return;
    }

    const platform: Platform = 'web';

    if (!isEventAllowed(event, platform)) {
      return;
    }

    const eventName = getEventName(event);

    if (!eventName) {
      return;
    }

    const trackPromise = analytics.track(eventName, properties);

    trackPromise?.catch(() => undefined);

    return trackPromise;
  },
  screen: (...args: Parameters<SegmentWebClient['page']>) => {
    if (!isSegmentUsable(analytics)) {
      return;
    }

    return analytics.page(...args);
  },
  identify: (...args: Parameters<SegmentWebClient['identify']>) => {
    if (!isSegmentUsable(analytics)) {
      return;
    }

    return analytics.identify(...args);
  },
  /**
   * Clears user identity. By default it rotates the anonymous ID — for genuine
   * logout, so the next person on a shared device starts fresh — or preserves it
   * when clearing a not-logged-in visitor (keeping their pre-signup events
   * grouped).
   *
   * The web SDK always clears the anonymous ID on reset, so to preserve it we
   * capture it beforehand and restore it after.
   */
  reset: async (resetAnonymousId: boolean = true) => {
    if (!isSegmentUsable(analytics)) {
      return;
    }

    userTraits = {};

    identifiedUserId = undefined;

    if (!resetAnonymousId) {
      const user = await analytics.user();

      const currentAnonId = user.anonymousId();

      await analytics.reset();

      if (currentAnonId) {
        user.anonymousId(currentAnonId);
      }

      return;
    }

    return analytics.reset();
  },

  /**
   * Returns the current anonymous ID from the Segment client.
   */
  getAnonymousId: async (): Promise<string | null | undefined> => {
    if (!isSegmentEnvEnabled || !analytics) {
      return undefined;
    }

    const user = await analytics.user();

    return user.anonymousId();
  },
};
